import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Calendar, BookOpen, MessageSquare, Video, Headphones, ListChecks, ExternalLink, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import ActivityDiscussion from "./ActivityDiscussion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const activityIcons = {
  reading: BookOpen,
  reflection: MessageSquare,
  task: ListChecks,
  quiz: ListChecks,
  video: Video,
  audio: Headphones,
};

export default function ActivityViewer({ courseId, lessonIndex, activities, user }) {
  const [completedActivities, setCompletedActivities] = useState([]);
  const [loading, setLoading] = useState({});
  const [discussionOpen, setDiscussionOpen] = useState(null); // activity_id of open discussion

  useEffect(() => {
    loadProgress();
  }, [courseId]);

  const loadProgress = async () => {
    try {
      const response = await fetch(`${API}/courses/${courseId}/activity-progress`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCompletedActivities(data.completed_activities || []);
      }
    } catch (error) {
      console.error('Failed to load activity progress');
    }
  };

  const markComplete = async (activityId) => {
    setLoading(prev => ({ ...prev, [activityId]: true }));
    try {
      const response = await fetch(`${API}/activities/${activityId}/complete`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setCompletedActivities([...completedActivities, activityId]);
        toast.success('Activity completed!');
      } else {
        toast.error(data.detail || 'Failed to mark complete');
      }
    } catch (error) {
      toast.error('Failed to mark activity complete');
    } finally {
      setLoading(prev => ({ ...prev, [activityId]: false }));
    }
  };

  // Filter activities for current lesson
  const lessonActivities = activities.filter(a => a.lesson_index === lessonIndex);
  
  // Group by day
  const groupedByDay = lessonActivities.reduce((acc, activity) => {
    const day = activity.day_number;
    if (!acc[day]) acc[day] = [];
    acc[day].push(activity);
    return acc;
  }, {});

  const completedCount = lessonActivities.filter(a => completedActivities.includes(a.activity_id)).length;
  const totalCount = lessonActivities.length;

  if (lessonActivities.length === 0) {
    return null;
  }

  return (
    <Card className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#D4A373]" />
          <h3 className="text-lg font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
            Daily Activities
          </h3>
        </div>
        <div className="text-sm text-[#588157]">
          {completedCount}/{totalCount} completed
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-[#E9EDC9] rounded-full h-2 mb-6">
        <div 
          className="bg-[#D4A373] h-2 rounded-full transition-all duration-300"
          style={{ width: `${totalCount > 0 ? (completedCount / totalCount * 100) : 0}%` }}
        />
      </div>

      <div className="space-y-4">
        {Object.entries(groupedByDay).sort(([a], [b]) => Number(a) - Number(b)).map(([day, dayActivities]) => (
          <div key={day} className="border-l-2 border-[#D4A373] pl-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-[#D4A373]">Day {day}</span>
              <span className="text-xs text-[#588157]">
                ({dayActivities.filter(a => completedActivities.includes(a.activity_id)).length}/{dayActivities.length})
              </span>
            </div>
            
            <div className="space-y-2">
              {dayActivities.map((activity) => {
                const IconComponent = activityIcons[activity.activity_type] || ListChecks;
                const isCompleted = completedActivities.includes(activity.activity_id);
                const isLoading = loading[activity.activity_id];
                
                return (
                  <div 
                    key={activity.activity_id}
                    data-testid={`activity-item-${activity.activity_id}`}
                    className={`p-4 rounded-xl transition-all duration-200 ${
                      isCompleted 
                        ? 'bg-[#D4A373]/10 border border-[#D4A373]/30' 
                        : 'bg-white border border-[#CCD5AE]/50 hover:border-[#D4A373]/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                        isCompleted ? 'bg-[#D4A373]/20' : 'bg-[#E9EDC9]'
                      }`}>
                        <IconComponent className={`w-5 h-5 ${isCompleted ? 'text-[#D4A373]' : 'text-[#588157]'}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            isCompleted 
                              ? 'bg-[#D4A373]/20 text-[#D4A373]' 
                              : 'bg-[#E9EDC9] text-[#588157]'
                          }`}>
                            {activity.activity_type}
                          </span>
                        </div>
                        <h4 className={`font-medium mt-1 ${isCompleted ? 'text-[#588157]' : 'text-[#344E41]'}`}>
                          {activity.title}
                        </h4>
                        <p className="text-sm text-[#588157] mt-1">{activity.description}</p>
                        
                        {activity.url && (
                          <a 
                            href={activity.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-[#3A5A40] hover:underline mt-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Open Resource
                          </a>
                        )}
                        
                        {/* Discuss this activity link */}
                        <button
                          onClick={() => setDiscussionOpen(activity)}
                          data-testid={`discuss-activity-${activity.activity_id}`}
                          className="inline-flex items-center gap-1.5 text-sm text-[#588157] hover:text-[#3A5A40] mt-2 transition-colors group"
                        >
                          <MessagesSquare className="w-4 h-4 group-hover:scale-110 transition-transform" />
                          <span className="underline-offset-2 group-hover:underline">Discuss this activity</span>
                        </button>
                      </div>
                      
                      {user?.role === 'student' && (
                        <Button
                          size="sm"
                          variant={isCompleted ? "ghost" : "outline"}
                          onClick={() => !isCompleted && markComplete(activity.activity_id)}
                          disabled={isCompleted || isLoading}
                          data-testid={`complete-activity-${activity.activity_id}`}
                          className={`flex-shrink-0 ${
                            isCompleted 
                              ? 'text-[#D4A373] cursor-default' 
                              : 'border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white'
                          }`}
                        >
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5" />
                          ) : isLoading ? (
                            <Circle className="w-5 h-5 animate-pulse" />
                          ) : (
                            <Circle className="w-5 h-5" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Activity Discussion Modal */}
      {discussionOpen && (
        <ActivityDiscussion
          activityId={discussionOpen.activity_id}
          activityTitle={discussionOpen.title}
          user={user}
          onClose={() => setDiscussionOpen(null)}
        />
      )}
    </Card>
  );
}
